import React from 'react';
import { Formik } from 'formik';
import { isEmpty, isNil, uniqBy } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import { configuredFieldSchema } from '~/components/SystemConfiguration/FnolConfiguration/fnolConfigurationValidationSchema';
import type { DocumentType } from '~/components/types/document-type';
import { reportAxiosError } from '~/Utils';

import DocumentTypeFormFields, { DOCUMENT_TYPE_FORMIK_IDS } from './DocumentTypeFormFields';

interface UpsertDocumentTypeDialogProps {
  documentTypesDict: Record<string, DocumentType>;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  documentType?: DocumentType;
}

const UpsertDocumentTypeDialog: React.FC<UpsertDocumentTypeDialogProps> = ({
  documentTypesDict,
  onClose,
  onSubmit,
  documentType,
}) => {
  return (
    <Formik
      initialValues={{
        [DOCUMENT_TYPE_FORMIK_IDS.DISPLAY_NAME]: documentType?.display_name ?? '',
        [DOCUMENT_TYPE_FORMIK_IDS.DESCRIPTION]: documentType?.description ?? '',
        [DOCUMENT_TYPE_FORMIK_IDS.KEY]: documentType?.key ?? '',
        [DOCUMENT_TYPE_FORMIK_IDS.IS_ALL_LOBS]: documentType?.is_all_lobs ?? false,
        [DOCUMENT_TYPE_FORMIK_IDS.LOBS]: documentType?.lobs ?? [],
        [DOCUMENT_TYPE_FORMIK_IDS.CLIVE_INSTRUCTIONS]: documentType?.clive_instructions ?? '',
        [DOCUMENT_TYPE_FORMIK_IDS.CONFIGURATION]: {
          configured_fields: documentType?.configuration?.configured_fields ?? [],
        },
      }}
      validationSchema={Yup.object().shape({
        [DOCUMENT_TYPE_FORMIK_IDS.DISPLAY_NAME]: Yup.string()
          .required('Required')
          .trim()
          .lowercase()
          .notOneOf(
            Object.values(documentTypesDict)
              .filter((r) => r.id !== documentType?.id)
              .map((r) => r.display_name.toLowerCase().trim()),
            'Must be unique'
          ),
        [DOCUMENT_TYPE_FORMIK_IDS.KEY]: Yup.string()
          .required('Required')
          .trim()
          .matches(/^[a-z0-9_]*$/, 'Can only contain lowercase letters, numbers and underscores')
          .notOneOf(
            Object.keys(documentTypesDict).filter((key) => key !== documentType?.key),
            'Must be unique'
          ),
        [DOCUMENT_TYPE_FORMIK_IDS.DESCRIPTION]: Yup.string().required('Required').trim(),
        [DOCUMENT_TYPE_FORMIK_IDS.IS_ALL_LOBS]: Yup.bool(),
        [DOCUMENT_TYPE_FORMIK_IDS.LOBS]: Yup.array().when('is_all_lobs', {
          is: false,
          then: Yup.array().min(1, 'Required'),
        }),
        [DOCUMENT_TYPE_FORMIK_IDS.CLIVE_INSTRUCTIONS]: Yup.string().nullable(),
        [DOCUMENT_TYPE_FORMIK_IDS.CONFIGURATION]: Yup.object().shape({
          configured_fields: Yup.array()
            .of(configuredFieldSchema({}))
            .test('configured-fields-unique-ids', 'Additional Fields keys must be unique', (value) =>
              isEmpty(value) ? true : uniqBy(value, 'id')?.length === value?.length
            ),
        }),
      })}
      onSubmit={async (values: Record<string, unknown>, formikProps) => {
        try {
          await onSubmit(values);
          onClose();
        } catch (error) {
          formikProps.setSubmitting(false);
          await reportAxiosError(error);
        }
      }}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          title={documentType ? 'Edit Document Type' : 'Set Document Type'}
          isDialog
          onClose={onClose}
          maxWidth="lg"
          fullWidth
          footerActions={
            <DialogFooterActions disabled={isSubmitting} onClickPrimary={handleSubmit} onClickSecondary={onClose} />
          }
        >
          <DocumentTypeFormFields
            isUpdateMode={!isNil(documentType)}
            originalIsAllLobs={documentType?.is_all_lobs}
            originalLobs={documentType?.lobs}
          />
        </CardDialog>
      )}
    </Formik>
  );
};

export default UpsertDocumentTypeDialog;
